import { SetOptional } from 'type-fest';

import {
  FormModal,
  FormModalProps,
} from '@/components/modals/FormModal/FormModal';
import { FormModalActions } from '@/components/modals/FormModal/FormModalActions/FormModalActions';
import {
  FeedbackMessages,
  useFeedback,
} from '@/components/notifications/Feedback/useFeedback';
import { FormProvider, useFormContext } from '@/components/react-hook-form';
import { useForm } from '@/components/react-hook-form';
import { useFormSaveHandler } from '@/hooks/useFormSaveHandler';
import { useReportError } from '@/hooks/useReportError';
import { DocumentSummaryPromptDocumentType } from '@/types/schema';

import { EMPTY_PROMPT_SENTINEL } from '../../AdminPromptIterationPage/constants';
import { CreateCustomSummaryPromptForm } from './CreateCustomSummaryPromptForm';
import { useCreateDocumentSummaryPromptMutation } from './graphql/CreateDocumentSummaryPrompt.generated';

type FormModalOptionalProps = SetOptional<FormModalProps, 'heading'>;
export type CreateCustomSummaryPromptModalProps = FormModalOptionalProps & {
  onCreated?: (id: string) => void;
};

export interface CreateCustomSummaryPromptFormShape {
  displayName: string;
  documentType: DocumentSummaryPromptDocumentType | null;
  shouldAutoRun: boolean;
  isDefaultForDocType: boolean;
}

function CreateCustomSummaryPromptModalComponent({
  onClose,
  onCreated,
  heading = 'Create custom prompt',
  ...formModalProps
}: CreateCustomSummaryPromptModalProps) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const { formRef, handleSave } = useFormSaveHandler();
  const { handleSubmit, reset, shouldBlockNavigation, formState } =
    useFormContext<CreateCustomSummaryPromptFormShape>();

  const [createPrompt] = useCreateDocumentSummaryPromptMutation({
    onCompleted: (data) => {
      showFeedback('Custom prompt created', { variant: 'success' });
      const id = data.createDocumentSummaryPrompt?.id;
      if (id) {
        closeModal();
        onCreated?.(id);
      }
    },
    onError: (err) => {
      showFeedback(FeedbackMessages.formSaveError);
      reportError('Could not create custom summary prompt', err);
    },
  });

  const onSubmit = handleSubmit((formData) => {
    if (!formData.documentType) {
      throw new Error('Document type is required');
    }

    return createPrompt({
      variables: {
        input: {
          create: {
            displayName: formData.displayName,
            documentType: formData.documentType,
            promptMarkdown: EMPTY_PROMPT_SENTINEL,
            isDefaultForDocType: formData.isDefaultForDocType,
            isSystemPrompt: false,
            shouldAutoRun: formData.shouldAutoRun,
          },
        },
      },
    });
  });

  const closeModal = () => {
    reset();
    onClose();
  };

  return (
    <FormModal
      {...formModalProps}
      onClose={onClose}
      heading={heading}
      shouldBlockClose={shouldBlockNavigation}
      actions={
        <FormModalActions.Provider formState={formState}>
          <FormModalActions.CancelButton onClick={closeModal} />
          <FormModalActions.SaveButton onClick={handleSave}>
            Create
          </FormModalActions.SaveButton>
        </FormModalActions.Provider>
      }
    >
      <form ref={formRef} onSubmit={onSubmit} noValidate>
        <CreateCustomSummaryPromptForm />
      </form>
    </FormModal>
  );
}

export const CreateCustomSummaryPromptModal = (
  props: CreateCustomSummaryPromptModalProps
) => {
  const formMethods = useForm<CreateCustomSummaryPromptFormShape>({
    defaultValues: {
      displayName: '',
      documentType: null,
      shouldAutoRun: false,
      isDefaultForDocType: false,
    },
  });

  return (
    <FormProvider {...formMethods}>
      <CreateCustomSummaryPromptModalComponent {...props} />
    </FormProvider>
  );
};
